import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  apiErrorMessage,
  cloudDeleteRole,
  cloudListRoles,
  cloudUpsertRole,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";

export type PermissionKey =
  | "dashboard"
  | "projects"
  | "devices"
  | "users"
  | "audit"
  | "resources"
  | "settings"
  | "alerts"
  | "tickets"
  | "billing"
  | "maintenance"
  | "debug";

export const permissionCatalog: {
  key: PermissionKey;
  label: string;
  group: string;
}[] = [
  { key: "dashboard", label: "查看总览", group: "基础" },
  { key: "projects", label: "管理项目", group: "基础" },
  { key: "devices", label: "管理设备", group: "基础" },
  { key: "users", label: "管理员工", group: "管理" },
  { key: "audit", label: "查看审计日志", group: "管理" },
  { key: "resources", label: "查看资源中心", group: "基础" },
  { key: "settings", label: "修改个人中心设置", group: "管理" },
  { key: "maintenance", label: "发起远程维护", group: "运维" },
  { key: "debug", label: "设备/场景/自动化调试", group: "运维" },
  { key: "alerts", label: "处理告警", group: "运维" },
  { key: "tickets", label: "客户支持", group: "运维" },
  { key: "billing", label: "查看计费", group: "管理" },
];

export interface RoleDef {
  id: string;
  name: string;
  description: string;
  builtin: boolean;
  permissions: PermissionKey[];
}

export const roleDisplayNames: Record<string, string> = {
  platform_admin: "平台管理员",
  integrator_admin: "组织管理员",
  integrator_tech: "技术员",
  integrator_support: "客服",
};

/** 管理员预置角色：名称与权限不可改。优先用稳定 key，其次种子 id 后缀。 */
const ADMIN_ROLE_KEYS = new Set(["integrator_admin", "platform_admin"]);

export function isAdminRole(role: { id: string; name: string }): boolean {
  if (ADMIN_ROLE_KEYS.has(role.name)) return true;
  return /_admin$/.test(role.id);
}

export function getRoleDisplayName(roleName: string): string {
  return roleDisplayNames[roleName] || roleName;
}

interface RolesState {
  roles: RoleDef[];
  syncing: boolean;
  upsert: (role: RoleDef) => Promise<RoleDef | null>;
  remove: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const RolesContext = createContext<RolesState | null>(null);

function mapRole(r: {
  id: string;
  name: string;
  description: string;
  builtin: boolean;
  permissions: string[];
}): RoleDef {
  return {
    id: r.id,
    name: r.name,
    description: r.description || "",
    builtin: r.builtin,
    permissions: r.permissions as PermissionKey[],
  };
}

export function RolesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setRoles([]);
      return;
    }
    setSyncing(true);
    try {
      const items = await cloudListRoles();
      setRoles(items.map(mapRole));
    } catch {
      /* keep current */
    } finally {
      setSyncing(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<RolesState>(
    () => ({
      roles,
      syncing,
      refresh,
      upsert: async (role) => {
        try {
          const saved = await cloudUpsertRole({
            id: role.id.startsWith("role_tmp") ? undefined : role.id,
            name: role.name,
            description: role.description,
            permissions: role.permissions,
          });
          const mapped = mapRole(saved);
          setRoles((prev) => {
            const idx = prev.findIndex((x) => x.id === mapped.id);
            if (idx < 0) return [...prev, mapped];
            const next = [...prev];
            next[idx] = mapped;
            return next;
          });
          return mapped;
        } catch (err) {
          console.error(apiErrorMessage(err, "保存角色失败"));
          return null;
        }
      },
      remove: async (id) => {
        try {
          await cloudDeleteRole(id);
          setRoles((prev) => prev.filter((x) => x.builtin || x.id !== id));
          return true;
        } catch (err) {
          console.error(apiErrorMessage(err, "删除角色失败"));
          return false;
        }
      },
    }),
    [roles, syncing, refresh],
  );

  return (
    <RolesContext.Provider value={value}>{children}</RolesContext.Provider>
  );
}

export function useRoles() {
  const ctx = useContext(RolesContext);
  if (!ctx) throw new Error("useRoles must be used within RolesProvider");
  return ctx;
}
